import { Layer } from "../../types/Editor";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Tabs, TabsContent } from "../../components/ui/tabs";
import { PlayerRef } from "@remotion/player";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { EditorHeader } from "../../components/EditorHeader";
import { LayerSettings } from "../../components/LayerSettings";
import { PreviewArea } from "../../components/PreviewArea";
import { Timeline } from "../../components/Timeline";
import { ToolBar } from "../../components/ToolBar";
import { ToastContainer } from "../Dashboard/DashboardComponents";
import { TransportControls } from "../../components/TransportControls";
import { projectService } from "../../services/project.service";
import { uploadService } from "../../services/upload.service";
import { exportService } from "../../services/export.service";
import { ExportRequest, User as UserType } from "../../types/api.types";
import { Loader2, AlertCircle, CheckCircle, Clock, X, Zap, Sparkles, Download, ArrowRight } from "lucide-react";
import { authService } from "../../services/auth.service";
import { useGlobalUpload } from "../../contexts/UploadContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import confetti from "canvas-confetti";
import { motion, AnimatePresence } from "framer-motion";

// Video State Type (Remotion Pattern)
type VideoState = { type: "empty" } | { type: "blob" | "cloud"; url: string };

export default function Editor() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  // Video & Timeline State
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoAspectRatio, setVideoAspectRatio] = useState(16 / 9);
  const [videoWidth, setVideoWidth] = useState(1920);
  const [videoHeight, setVideoHeight] = useState(1080);
  const [zoom, setZoom] = useState(2);
  const [currentTime, setCurrentTime] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // Layer State
  const [layers, setLayers] = useState<Layer[]>([]);
  const layersRef = useRef(layers);
  useEffect(() => {
    layersRef.current = layers;
  }, [layers]);
  const [activeLayerId, setActiveLayerId] = useState<string | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingStart, setDrawingStart] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [currentTool, setCurrentTool] = useState<
    "highlight" | "zoom" | "blur" | null
  >(null);
  const [cursorPosition, setCursorPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartXRef = useRef<number>(0);
  const dragStartLayer = useRef<Layer | null>(null);
  const [resizeHandle, setResizeHandle] = useState<
    "intro" | "main-left" | "main-right" | "outro" | null
  >(null);

  // History State
  const [history, setHistory] = useState<Layer[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // UI State
  const [activeTab, setActiveTab] = useState<"effects" | "video">("effects");
  const [showVideoGenerator, setShowVideoGenerator] = useState(false);
  const [zoomAnchor, setZoomAnchor] = useState<{ time: number; offsetPx: number } | null>(null);

  // Video Upload State (Remotion pattern)
  const [videoState, setVideoState] = useState<VideoState>({ type: "empty" });
  const [videoSrc, setVideoSrc] = useState<string | null>(null);

  // API State
  const { uploadState, startUpload } = useGlobalUpload();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projectTitle, setProjectTitle] = useState("");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [exportStatus, setExportStatus] = useState<ExportRequest | null>(null);
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [user, setUser] = useState<UserType | null>(null);

  const loadUserProfile = useCallback(async () => {
    try {
      const userData = await authService.getProfile();
      setUser(userData);
    } catch (err: any) {
      console.error("Failed to load profile in editor:", err);
    }
  }, []);

  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  // Refs
  const timelineRef = useRef<HTMLDivElement>(null);
  const timelineContentRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<PlayerRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const autosaveTimer = useRef<NodeJS.Timeout>();
  const exportPollingInterval = useRef<NodeJS.Timeout>();
  // Stores the frame to seek to on the NEXT play press (one-shot, cleared after use)
  const pendingLayerSeek = useRef<number | null>(null);

  const FPS = 30;
  const visibleDuration = videoDuration / zoom;

  // Helper to get the active layer object by id
  const activeLayer = React.useMemo(
    () => layers.find((l) => l.id === activeLayerId) || null,
    [layers, activeLayerId]
  );

  const safeNavigate = useCallback(
    (to: string) => {
      navigate(to);
    },
    [navigate]
  );



  // ============================================
  // API INTEGRATION: Load Project Data
  // ============================================
  useEffect(() => {
    const loadProject = async () => {
      if (!projectId) {
        setError("No project ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const project = await projectService.getProject(projectId);

        setProjectTitle(project.title);

        if (project.compositionSettings) {
          const { videoUrl, layers: projectLayers } =
            project.compositionSettings;

          if (videoUrl) {
            setVideoSrc(videoUrl);
            setVideoState({ type: "cloud", url: videoUrl });
            // Load video to get duration
            const video = document.createElement("video");
            video.src = videoUrl;
            await new Promise((resolve) => {
              video.onloadedmetadata = resolve;
            });
            const dur = video.duration;
            setVideoDuration(dur);
            
            // dynamically calculate zoom based on video length
            // so a 3-second layer is roughly 250-300px wide
            const optimalZoom = Math.ceil(dur * 0.0916);
            setZoom(Math.max(2, optimalZoom));
            setVideoAspectRatio(video.videoWidth / video.videoHeight);
            if (project.compositionSettings.width) setVideoWidth(project.compositionSettings.width);
            else setVideoWidth(video.videoWidth || 1920);
            if (project.compositionSettings.height) setVideoHeight(project.compositionSettings.height);
            else setVideoHeight(video.videoHeight || 1080);
          }

          if (projectLayers && projectLayers.length > 0) {
            setLayers(projectLayers);
            setHistory([projectLayers]);
          }
        }

        setError(null);
      } catch (err: any) {
        console.error("Failed to load project:", err);
        setError(err.message || "Failed to load project");

        // If project not found, redirect to dashboard
        if (err.message?.includes("not found")) {
          setTimeout(() => safeNavigate("/dashboard"), 2000);
        }
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [projectId, safeNavigate]);

  // ============================================
  // API INTEGRATION: Autosave Project
  // ============================================
  useEffect(() => {
    // Don't autosave if loading or no project
    if (loading || !projectId || videoState.type !== "cloud") return;
    // Clear existing timer
    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
    }

    // Set new autosave timer (2 seconds after last change)
    autosaveTimer.current = setTimeout(async () => {
      try {
        setSaving(true);
        await projectService.autosaveProject(projectId, {
          videoUrl: videoSrc,
          layers,
          fps: FPS,
          width: videoWidth,
          height: videoHeight,
          duration: videoDuration,
        });
        setLastSaved(new Date());
        console.log("Project autosaved successfully");
      } catch (err: any) {
        console.error("Autosave failed:", err);
        // Don't show error to user for autosave failures
      } finally {
        setSaving(false);
      }
    }, 2000);

    return () => {
      if (autosaveTimer.current) {
        clearTimeout(autosaveTimer.current);
      }
    };
  }, [layers, videoSrc, videoDuration, projectId, loading, videoState.type]);

  // ============================================
  // Auto-select current layer on pause
  // ============================================
  useEffect(() => {
    if (isPlaying || isDragging || isDrawing) return;

    // Check if the current active layer is still relevant to the current time.
    // If it is, we stick with it to respect manual selection precedence.
    if (activeLayerId) {
      const currentActive = layers.find((l) => l.id === activeLayerId);
      if (currentActive) {
        const totalDuration =
          currentActive.introDuration +
          currentActive.mainDuration +
          currentActive.outroDuration;
        const EPSILON = 0.001;
        if (
          currentTime >= currentActive.start - EPSILON &&
          currentTime <= currentActive.start + totalDuration + EPSILON
        ) {
          return;
        }
      }
    }

    const findOptimalLayer = () => {
      const activeAtTime = layers.filter((layer) => {
        const totalDuration =
          layer.introDuration + layer.mainDuration + layer.outroDuration;
        return (
          currentTime >= layer.start &&
          currentTime <= layer.start + totalDuration
        );
      });

      if (activeAtTime.length === 0) return null;

      // Define precedence: highlight > blur > zoom
      const precedence: Record<string, number> = {
        highlight: 3,
        blur: 2,
        zoom: 1,
      };

      return activeAtTime.sort(
        (a, b) => (precedence[b.type] || 0) - (precedence[a.type] || 0)
      )[0];
    };

    const optimalLayer = findOptimalLayer();
    if (optimalLayer && optimalLayer.id !== activeLayerId) {
      setActiveLayerId(optimalLayer.id);
    }
  }, [isPlaying, isDragging, isDrawing, currentTime, layers, activeLayerId]);

  // ============================================
  // API INTEGRATION: Video Upload (S3 direct + Remotion optimistic updates)
  // ============================================
  const handleVideoUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !projectId) return;

      try {
        setError(null);

        // -------------------------------------------------
        // 1. OPTIMISTIC PREVIEW (blob URL)
        // -------------------------------------------------
        const blobUrl = URL.createObjectURL(file);
        setVideoState({ type: "blob", url: blobUrl });

        // Load metadata for immediate UI updates
        const videoEl = document.createElement("video");
        videoEl.src = blobUrl;
        await new Promise<void>((resolve) => {
          videoEl.onloadedmetadata = () => resolve();
        });

        const dur = videoEl.duration;
        setVideoSrc(blobUrl);
        setVideoDuration(dur);
        
        // dynamically calculate zoom based on video length
        // so a 3-second layer is roughly 250-300px wide
        const optimalZoom = Math.ceil(dur * 0.0916);
        setZoom(Math.max(2, optimalZoom));
        setVideoAspectRatio(videoEl.videoWidth / videoEl.videoHeight);
        setVideoWidth(videoEl.videoWidth || 1920);
        setVideoHeight(videoEl.videoHeight || 1080);

        // Clear file input
        if (event.target) event.target.value = "";

        // -------------------------------------------------
        // 2. DISPATCH TO GLOBAL UPLOAD CONTEXT
        // -------------------------------------------------
        startUpload(projectId, file, (cloudUrl) => {
          setVideoState({ type: "cloud", url: cloudUrl });
          setVideoSrc(cloudUrl);
          loadUserProfile(); // Refresh storage usage
          URL.revokeObjectURL(blobUrl); // free memory
        });

      } catch (err: any) {
        console.error("Video upload failed:", err);
        setError(err.message || "Failed to start upload");
      }
    },
    // Dependencies
    [projectId, startUpload]
  );
  // ============================================
  // API INTEGRATION: Video Export
  // ============================================
  const triggerConfetti = useCallback(() => {
    const duration = 5 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const interval: any = setInterval(function () {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 250);
  }, []);


  const handlePublish = useCallback(async () => {
    if (!projectId) return;

    // Prevent multiple exports
    if (exportStatus?.status === 'pending' || exportStatus?.status === 'queued' || exportStatus?.status === 'processing') {
      setShowVideoGenerator(true); // Re-open the modal if clicking while somehow enabled
      return;
    }

    try {
      setError(null);

      // Check subscription before exporting
      const profile = await authService.getProfile();

      const isTrialActive = profile.subscriptionStatus === 'trial' && (!profile.trialEndsAt || new Date(profile.trialEndsAt) > new Date());
      const isSubscriptionActive = profile.subscriptionStatus === 'active';
      const isCancelledButActive = profile.subscriptionStatus === 'cancelled' && profile.renewsAt && new Date(profile.renewsAt) > new Date();

      if (!isTrialActive && !isSubscriptionActive && !isCancelledButActive) {
        setShowUpgradeModal(true);
        return;
      }

      // Start export
      const exportRequest = await exportService.exportVideo(projectId);
      setExportStatus(exportRequest);
      setShowVideoGenerator(true);
      triggerConfetti();

      // Poll for export status
      exportPollingInterval.current = setInterval(async () => {
        try {
          const status = await exportService.getExportStatus(exportRequest.id);
          setExportStatus(status);

          // Stop polling if completed or failed
          if (status.status === "completed" || status.status === "failed") {
            if (status.status === "completed") {
              triggerConfetti();
              loadUserProfile(); // Refresh storage usage
            }
            if (exportPollingInterval.current) {
              clearInterval(exportPollingInterval.current);
            }
          }
        } catch (err) {
          console.error("Failed to check export status:", err);
        }
      }, 5000); // Poll every 5 seconds
    } catch (err: any) {
      console.error("Export failed:", err);
      if (err.message?.includes("subscription") || err.code === 'SUBSCRIPTION_REQUIRED') {
        setShowUpgradeModal(true);
      } else {
        setError(err.message || "Failed to start export");
      }
    }
  }, [projectId, exportStatus]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (exportPollingInterval.current) {
        clearInterval(exportPollingInterval.current);
      }
    };
  }, []);

  // ============================================
  // EXISTING FUNCTIONALITY (Keep as is)
  // ============================================

  const handleFrameChange = useCallback(
    (frame: number) => {
      const newTime = frame / FPS;
      setCurrentTime(newTime);
    },
    [FPS]
  );

  const addToHistory = useCallback(
    (newLayers: Layer[]) => {
      setHistory((prevHistory) => {
        if (
          historyIndex >= 0 &&
          JSON.stringify(prevHistory[historyIndex]) ===
          JSON.stringify(newLayers)
        ) {
          return prevHistory;
        }
        const newHistory = prevHistory.slice(0, historyIndex + 1);
        newHistory.push(newLayers);
        return newHistory;
      });
      setHistoryIndex((prevIndex) => prevIndex + 1);
    },
    [historyIndex]
  );

  const handleUndo = useCallback(() => {
    setHistoryIndex((prevIndex) => {
      if (prevIndex > 0) {
        const newIndex = prevIndex - 1;
        setLayers(() => {
          if (Array.isArray(history[newIndex])) {
            return history[newIndex];
          }
          return [];
        });
        setActiveLayerId(null);
        return newIndex;
      }
      return prevIndex;
    });
  }, [history]);

  const handleRedo = useCallback(() => {
    setHistoryIndex((prevIndex) => {
      if (prevIndex < history.length - 1) {
        const newIndex = prevIndex + 1;
        setLayers(() => {
          if (Array.isArray(history[newIndex])) {
            return history[newIndex];
          }
          return [];
        });
        setActiveLayerId(null);
        return newIndex;
      }
      return prevIndex;
    });
  }, [history]);

  const formatTime = useCallback((seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  }, []);

  const handleTimelineClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!timelineContentRef.current || !playerRef.current) return;
      const rect = timelineContentRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const visibleDuration = videoDuration / zoom;
      const newPosition = scrollPosition + (x / rect.width) * visibleDuration;
      const clampedPosition = Math.max(0, Math.min(newPosition, videoDuration));
      const frame = Math.floor(clampedPosition * FPS);
      playerRef.current.seekTo(frame);
      handleFrameChange(frame);
    },
    [videoDuration, zoom, scrollPosition, FPS, handleFrameChange]
  );

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const scrollLeft = e.currentTarget.scrollLeft;
      const maxScroll =
        e.currentTarget.scrollWidth - e.currentTarget.clientWidth;
      setScrollPosition(
        (scrollLeft / maxScroll) * (videoDuration - visibleDuration)
      );
    },
    [videoDuration, visibleDuration]
  );

  const handleZoomChange = useCallback((newZoom: number) => {
    if (!timelineRef.current) return;

    const timeline = timelineRef.current;
    const rect = timeline.getBoundingClientRect();
    const viewportWidth = rect.width;
    const totalScrollWidth = timeline.scrollWidth;

    // Determine the anchor point (playhead if visible, otherwise center of viewport)
    const scrubberPosPx = (currentTime / videoDuration) * totalScrollWidth;
    const scrollLeft = timeline.scrollLeft;

    let anchorTime: number;
    let anchorOffsetPx: number;

    if (scrubberPosPx >= scrollLeft && scrubberPosPx <= scrollLeft + viewportWidth) {
      // Playhead is visible, anchor to it
      anchorTime = currentTime;
      anchorOffsetPx = scrubberPosPx - scrollLeft;
    } else {
      // Playhead not visible, anchor to center of viewport
      const centerPx = scrollLeft + viewportWidth / 2;
      anchorTime = (centerPx / totalScrollWidth) * videoDuration;
      anchorOffsetPx = viewportWidth / 2;
    }

    setZoomAnchor({ time: anchorTime, offsetPx: anchorOffsetPx });
    setZoom(newZoom);
  }, [currentTime, videoDuration]);

  // Use useLayoutEffect to adjust scroll position immediately after zoom (and width) changes
  React.useLayoutEffect(() => {
    if (!zoomAnchor || !timelineRef.current || !videoDuration) return;

    const timeline = timelineRef.current;
    const totalScrollWidth = timeline.scrollWidth;

    // Calculate new scroll position: newAnchorPx - originalOffsetPx
    const newAnchorPx = (zoomAnchor.time / videoDuration) * totalScrollWidth;
    const newScrollLeft = newAnchorPx - zoomAnchor.offsetPx;

    timeline.scrollLeft = newScrollLeft;

    // Clear anchor after applying
    setZoomAnchor(null);
  }, [zoom, videoDuration, zoomAnchor]);

  const handlePreviewMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      setCursorPosition({ x, y });

      if (isDrawing && drawingStart && activeLayer) {
        const endX = x;
        const endY = y;

        const updatedLayer = {
          ...activeLayer,
          data: {
            ...activeLayer.data,
            x: Math.min(drawingStart.x, endX),
            y: Math.min(drawingStart.y, endY),
            width: Math.abs(endX - drawingStart.x),
            height: Math.abs(endY - drawingStart.y),
          },
        };

        setLayers((prevLayers) =>
          prevLayers.map((layer) =>
            layer.id === activeLayer.id ? updatedLayer : layer
          )
        );
      }
    },
    [isDrawing, drawingStart, activeLayer]
  );

  const handlePreviewMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!currentTool) return;
      
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = (e.clientX - rect.left) / rect.width;
      const clickY = (e.clientY - rect.top) / rect.height;

      const layerExistsAtCurrentTime = layers.some((layer) => {
        const totalLayerDuration =
          layer.introDuration + layer.mainDuration + layer.outroDuration;
        return (
          layer.type === currentTool &&
          currentTime >= layer.start &&
          currentTime <= layer.start + totalLayerDuration
        );
      });

      if (layerExistsAtCurrentTime) {
        setCurrentTool(null);
        return;
      }

      setIsDrawing(true);
      setDrawingStart(cursorPosition);

      // Adjust start time so that mainDuration starts at currentTime
      // For highlight/zoom, introDuration is currently 0.5s
      const introDuration = currentTool === "blur" ? 0 : 0.5;
      const adjustedStart = Math.max(0, currentTime - introDuration);

      const newLayer: Layer = {
        id: Date.now().toString(),
        type: currentTool,
        start: adjustedStart,
        introDuration: introDuration,
        mainDuration: 2,
        outroDuration: currentTool === "blur" ? 0 : 0.5,
        data: {
          x: clickX,
          y: clickY,
          width: 0,
          height: 0,
          color: currentTool === "highlight" ? "#4f46e5" : undefined,
          transparency: 50,
          zoomFactor: currentTool === "zoom" ? 1.5 : undefined,
          blurAmount: currentTool === "blur" ? 10 : undefined,
          // Add these two lines for zoom:
          centerX: currentTool === "zoom" ? clickX : undefined,
          centerY: currentTool === "zoom" ? clickY : undefined,
        },
      };

      setActiveLayerId(newLayer.id);
      setLayers((prev) => [...prev, newLayer]);
    },
    [currentTool, currentTime, cursorPosition, layers]
  );

  const handlePreviewMouseUp = useCallback(() => {
    if (isDrawing && activeLayer) {
      setLayers((prevLayers) => {
        const newLayers = prevLayers.map((layer) => {
          if (layer.id === activeLayer.id) {
            const updatedLayer = {
              ...activeLayer,
              data: {
                ...activeLayer.data,
                width: Math.max(activeLayer.data.width, 0.1),
                height: Math.max(
                  activeLayer.data.height,
                  layer.type === "zoom" ? 0.1 : 0.07
                ),
              },
            };
            return updatedLayer;
          }
          return layer;
        });
        addToHistory(newLayers);
        return newLayers;
      });
    }
    setIsDrawing(false);
    setDrawingStart(null);
    setCurrentTool(null);
  }, [isDrawing, activeLayer, addToHistory]);

  const handlePlayPause = useCallback(() => {
    if (playerRef.current) {
      if (isPlaying) {
        playerRef.current.pause();
      } else {
        // One-shot: if a layer was recently selected, seek to its very start before playing
        if (pendingLayerSeek.current !== null) {
          const frame = pendingLayerSeek.current;
          pendingLayerSeek.current = null; // consume immediately
          playerRef.current.seekTo(frame);
          handleFrameChange(frame);
        }
        playerRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying, handleFrameChange]);

  const handleLayerMouseDown = useCallback(
    (e: React.MouseEvent, layer: Layer) => {
      e.stopPropagation();
      if (e.button !== 0) return;
      setActiveLayerId(layer.id);
      setIsDragging(true);
      dragStartXRef.current = e.clientX;
      dragStartLayer.current = layer;
      setResizeHandle(null);

      // Queue seek to layer.start for the next Play press
      pendingLayerSeek.current = Math.floor(layer.start * FPS);
      // Preview: seek to start of main duration now
      if (playerRef.current) {
        const previewFrame = Math.ceil((layer.start + layer.introDuration) * FPS);
        playerRef.current.seekTo(previewFrame);
        handleFrameChange(previewFrame);
      }
    },
    [FPS, handleFrameChange]
  );

  const handleLayerMouseMove = useCallback(
    (e: MouseEvent) => {
      if (
        isDragging &&
        activeLayerId &&
        !resizeHandle &&
        timelineContentRef.current
      ) {
        const rect = timelineContentRef.current.getBoundingClientRect();
        const deltaX = e.clientX - dragStartXRef.current;
        const deltaTime = (deltaX / rect.width) * videoDuration;

        setLayers((prevLayers) => {
          const newLayers = prevLayers.map((layer) => {
            if (layer.id === activeLayerId && dragStartLayer.current) {
              const original = dragStartLayer.current;
              return {
                ...layer,
                start: Math.max(
                  0,
                  Math.min(
                    videoDuration -
                    original.introDuration -
                    original.mainDuration -
                    original.outroDuration,
                    original.start + deltaTime
                  )
                ),
              };
            }
            return layer;
          });
          return newLayers;
        });
      }
    },
    [
      isDragging,
      activeLayerId,
      resizeHandle,
      visibleDuration,
      videoDuration,
    ]
  );

  const handleResizeMouseDown = useCallback(
    (
      e: React.MouseEvent,
      layer: Layer,
      handle: "intro" | "main-left" | "main-right" | "outro" | null
    ) => {
      e.stopPropagation();
      e.preventDefault();
      if (e.button !== 0) return;
      setActiveLayerId(layer.id);
      setResizeHandle(handle);
      setIsDragging(true);
      dragStartXRef.current = e.clientX;
      dragStartLayer.current = layer;

      // Queue seek to layer.start for the next Play press
      pendingLayerSeek.current = Math.floor(layer.start * FPS);
      // Preview: seek to start of main duration now
      if (playerRef.current) {
        const previewFrame = Math.ceil((layer.start + layer.introDuration) * FPS);
        playerRef.current.seekTo(previewFrame);
        handleFrameChange(previewFrame);
      }
    },
    [FPS, handleFrameChange]
  );

  const handleResizeMouseMove = useCallback(
    (e: MouseEvent) => {
      if (
        isDragging &&
        activeLayerId &&
        resizeHandle &&
        timelineContentRef.current
      ) {
        const rect = timelineContentRef.current.getBoundingClientRect();
        const deltaX = e.clientX - dragStartXRef.current;
        const deltaTime = (deltaX / rect.width) * videoDuration;

        setLayers((prevLayers) => {
          const newLayers = prevLayers.map((layer) => {
            if (layer.id === activeLayerId && dragStartLayer.current) {
              const original = dragStartLayer.current;
              const newLayer = { ...layer };
              const minDuration = 0.1;

              if (original.type === "blur") {
                // Special handling for blur: no intro/outro, only main resizing
                switch (resizeHandle) {
                  case "intro":
                  case "main-left": {
                    const maxShift = original.start + original.mainDuration - minDuration;
                    const adjustedDeltaTime =
                      deltaX < 0
                        ? Math.max(deltaTime, -original.start)
                        : Math.min(deltaTime, maxShift);
                    newLayer.start = original.start + adjustedDeltaTime;
                    newLayer.mainDuration = original.mainDuration - adjustedDeltaTime;
                    break;
                  }
                  case "outro":
                  case "main-right":
                    newLayer.mainDuration = Math.max(
                      minDuration,
                      original.mainDuration + deltaTime
                    );
                    break;
                }
              } else {
                // Default handling for highlight and zoom
                switch (resizeHandle) {
                  case "intro": {
                    const maxShift = original.start;
                    const adjustedDeltaTime =
                      deltaX < 0 ? Math.max(deltaTime, -maxShift) : deltaTime;
                    newLayer.start = original.start + adjustedDeltaTime;
                    newLayer.introDuration = Math.max(
                      minDuration,
                      original.introDuration - adjustedDeltaTime
                    );
                    break;
                  }
                  case "main-left": {
                    const leftDelta = Math.min(
                      original.mainDuration - minDuration,
                      deltaTime
                    );
                    newLayer.introDuration = Math.max(
                      minDuration,
                      original.introDuration + leftDelta
                    );
                    newLayer.mainDuration = original.mainDuration - leftDelta;
                    break;
                  }
                  case "main-right":
                    newLayer.mainDuration = Math.max(
                      minDuration,
                      original.mainDuration + deltaTime
                    );
                    break;
                  case "outro":
                    newLayer.outroDuration = Math.max(
                      minDuration,
                      original.outroDuration + deltaTime
                    );
                    break;
                }
              }

              const totalDuration =
                newLayer.introDuration +
                newLayer.mainDuration +
                newLayer.outroDuration;
              if (newLayer.start + totalDuration > videoDuration) {
                const overflow = newLayer.start + totalDuration - videoDuration;
                if (resizeHandle === "main-right" || resizeHandle === "outro") {
                  newLayer.outroDuration = Math.max(
                    minDuration,
                    newLayer.outroDuration - overflow
                  );
                } else {
                  newLayer.mainDuration = Math.max(
                    minDuration,
                    newLayer.mainDuration - overflow
                  );
                }
              }

              return newLayer;
            }
            return layer;
          });
          return newLayers;
        });
      }
    },
    [
      isDragging,
      activeLayerId,
      resizeHandle,
      visibleDuration,
      videoDuration,
    ]
  );

  const handleGlobalMouseUpCommit = useCallback(() => {
    if (isDragging) {
      addToHistory(layersRef.current);
    }
    setIsDragging(false);
    setResizeHandle(null);
  }, [isDragging, addToHistory]);

  const handleGlobalMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        if (resizeHandle) {
          handleResizeMouseMove(e);
        } else {
          handleLayerMouseMove(e);
        }
      }
    },
    [isDragging, resizeHandle, handleResizeMouseMove, handleLayerMouseMove]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        e.preventDefault();
        handlePlayPause();
      } else if ((e.ctrlKey || e.metaKey) && e.code === "KeyZ") {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handlePlayPause, handleUndo, handleRedo]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleGlobalMouseMove);
      window.addEventListener("mouseup", handleGlobalMouseUpCommit);
    }
    return () => {
      window.removeEventListener("mousemove", handleGlobalMouseMove);
      window.removeEventListener("mouseup", handleGlobalMouseUpCommit);
    };
  }, [isDragging, handleGlobalMouseMove, handleGlobalMouseUpCommit]);

  useEffect(() => {
    if (activeLayerId && !layers.some((l) => l.id === activeLayerId)) {
      setActiveLayerId(null);
    }
  }, [layers, activeLayerId]);

  const handleLayerUpdate = useCallback(
    (updatedLayer: Layer) => {
      const {
        start,
        introDuration,
        mainDuration,
        outroDuration,
        data: { transparency, zoomFactor },
      } = updatedLayer;
      if (
        start < 0 ||
        introDuration < 0 ||
        mainDuration < 0 ||
        outroDuration < 0 ||
        start + introDuration + mainDuration + outroDuration > videoDuration ||
        transparency < 10 ||
        zoomFactor < 1.1
      ) {
        return;
      }
      setLayers((prevLayers) => {
        const newLayers = prevLayers.map((layer) =>
          layer.id === updatedLayer.id ? updatedLayer : layer
        );
        addToHistory(newLayers);
        return newLayers;
      });
    },
    [addToHistory, videoDuration]
  );

  const handleLayerDelete = useCallback(
    (layerId: string) => {
      setLayers((prevLayers) => {
        const newLayers = prevLayers.filter((l) => l.id !== layerId);
        addToHistory(newLayers);
        return newLayers;
      });
      setActiveLayerId(null);
    },
    [addToHistory]
  );

  const handleToolSelect = useCallback(
    (tool: "highlight" | "zoom" | "blur" | null) => {
      setCurrentTool(tool);
    },
    []
  );

  // ============================================
  // LOADING STATE
  // ============================================
  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-6">
          <div className="relative w-16 h-16">
            <Loader2 className="h-16 w-16 animate-spin text-indigo-600" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 bg-white rounded-full" />
            </div>
          </div>
          <div className="text-center">
            <h3 className="text-lg font-bold text-slate-900">Entering Studio</h3>
            <p className="text-slate-400 text-sm font-medium mt-1">Preparing your creative space...</p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // ERROR STATE
  // ============================================
  if (error && !projectTitle) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-6 max-w-sm text-center bg-white p-10 rounded-[32px] border border-slate-200 shadow-xl">
          <div className="w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center text-rose-500">
            <AlertCircle size={32} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Studio Occurred an Error</h2>
            <p className="text-slate-500 text-sm font-medium mt-2">{error}</p>
          </div>
          <Button
            onClick={() => safeNavigate("/dashboard")}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white h-12 rounded-xl font-bold transition-all"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen flex flex-col bg-slate-50 font-sans overflow-hidden">
      {/* Header */}
      <EditorHeader
        onPreview={() => playerRef.current?.requestFullscreen()}
        onPublish={handlePublish}
        safeNavigate={safeNavigate}
        projectTitle={projectTitle}
        saving={saving}
        uploading={uploadState.status === "uploading"}
        exporting={exportStatus?.status === "pending" || exportStatus?.status === "queued" || exportStatus?.status === "processing"}
        lastSaved={lastSaved as any}
      />

      <ToastContainer />

      {/* Upload/Status Banner */}
      <AnimatePresence>
        {uploadState.status === "uploading" && uploadState.projectId === projectId && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-indigo-600 text-white overflow-hidden shadow-lg shadow-indigo-100 relative z-40"
          >
            <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <Loader2 size={16} className="animate-spin text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold leading-none">
                    {videoState.type === "blob" ? "Cloud Syncing" : "Finalizing Video"}
                  </p>
                  <p className="text-[10px] font-bold text-indigo-100 uppercase tracking-widest mt-1.5 opacity-80">
                    {videoState.type === "blob" ? "Uploading to secure studio cloud" : "Optimizing metadata for studio quality"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="flex flex-col items-end gap-1.5">
                  <span className="text-xs font-bold tabular-nums">{uploadState.progress}%</span>
                  <div className="w-48 h-1.5 bg-indigo-400 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-white rounded-full shadow-[0_0_10px_white]"
                      initial={{ width: 0 }}
                      animate={{ width: `${uploadState.progress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Workspace */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 flex overflow-hidden p-4 gap-4">
          {/* Left Toolbar */}
          <div className="flex flex-col justify-center">
            <ToolBar
              currentTool={currentTool}
              onToolSelect={handleToolSelect}
            />
          </div>

          {/* Center: Preview Area */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex-1 min-h-0">
              <Card className="h-full rounded-[24px] border-slate-200 overflow-hidden shadow-sm bg-white p-0 flex flex-col">
                <PreviewArea
                  videoSrc={videoSrc}
                  layers={layers}
                  isPlaying={isPlaying}
                  playerRef={playerRef}
                  currentTool={currentTool}
                  isDrawing={isDrawing}
                  activeLayer={activeLayer}
                  cursorPosition={cursorPosition}
                  videoAspectRatio={videoAspectRatio}
                  videoWidth={videoWidth}
                  videoHeight={videoHeight}
                  videoDuration={videoDuration}
                  previewRef={previewRef}
                  onMouseDown={handlePreviewMouseDown}
                  onMouseMove={handlePreviewMouseMove}
                  onMouseUp={handlePreviewMouseUp}
                  onMouseLeave={handlePreviewMouseUp}
                  onVideoUpload={handleVideoUpload}
                  onLayerUpdate={handleLayerUpdate}
                  setIsPlaying={setIsPlaying}
                  setCurrentTime={setCurrentTime}
                  fileInputRef={fileInputRef}
                />
              </Card>
            </div>
          </div>

          {/* Right Settings */}
          <div className="h-full">
            <LayerSettings
              activeLayer={activeLayer}
              onLayerUpdate={handleLayerUpdate}
              onLayerDelete={handleLayerDelete}
              videoSrc={videoSrc}
              onLayerFocus={(layer) => {
                setActiveLayerId(layer.id);
                pendingLayerSeek.current = Math.floor(layer.start * FPS);
                if (playerRef.current) {
                  const previewFrame = Math.ceil((layer.start + layer.introDuration) * FPS);
                  playerRef.current.seekTo(previewFrame);
                  handleFrameChange(previewFrame);
                }
                if (timelineRef.current && videoDuration > 0) {
                  const timeline = timelineRef.current;
                  const totalScrollWidth = timeline.scrollWidth;
                  const viewportWidth = timeline.clientWidth;
                  const totalDuration = layer.introDuration + layer.mainDuration + layer.outroDuration;
                  const layerStartPx = (layer.start / videoDuration) * totalScrollWidth;
                  const layerEndPx = ((layer.start + totalDuration) / videoDuration) * totalScrollWidth;
                  const scrollLeft = timeline.scrollLeft;
                  const isVisible = layerStartPx >= scrollLeft && layerEndPx <= scrollLeft + viewportWidth;
                  if (!isVisible) {
                    const layerCenterPx = (layerStartPx + layerEndPx) / 2;
                    timeline.scrollTo({
                      left: Math.max(0, layerCenterPx - viewportWidth / 2),
                      behavior: 'smooth',
                    });
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Bottom Workspace: Controls & Timeline (Full Width) */}
        <div className="px-4 pb-4 flex flex-col gap-2">
          <TransportControls
            playerRef={playerRef}
            durationInFrames={videoDuration * FPS}
            fps={FPS}
            isPlaying={isPlaying}
            historyIndex={historyIndex}
            historyLength={history.length}
            zoom={zoom}
            onPlayPause={handlePlayPause}
            onSkipToStart={() => playerRef.current?.seekTo(0)}
            onSkipToEnd={() =>
              playerRef.current?.seekTo(Math.floor(videoDuration * 30))
            }
            onUndo={handleUndo}
            onRedo={handleRedo}
            onZoomChange={handleZoomChange}
          />

          <Timeline
            layers={layers}
            currentTime={currentTime}
            scrollPosition={scrollPosition}
            visibleDuration={visibleDuration}
            videoDuration={videoDuration}
            zoom={zoom}
            timelineRef={timelineRef}
            timelineContentRef={timelineContentRef}
            onTimelineClick={handleTimelineClick}
            onScroll={handleScroll}
            onLayerMouseDown={handleLayerMouseDown}
            onResizeMouseDown={handleResizeMouseDown}
            formatTime={formatTime}
            playerRef={playerRef}
            onFrameChange={handleFrameChange}
          />
        </div>
      </div>



      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-md bg-white border-0 shadow-2xl p-0 overflow-hidden rounded-[40px]">
          <div className="bg-gradient-to-br from-indigo-50/50 via-white to-white p-10 text-center relative">
            <div className="absolute top-6 right-6">
              <button onClick={() => setShowUpgradeModal(false)} className="text-slate-300 hover:text-slate-600 transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="w-20 h-20 bg-indigo-600 rounded-[32px] flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-indigo-200 transform -rotate-6">
              <Zap className="w-10 h-10 text-white fill-white" />
            </div>
            <DialogHeader>
              <DialogTitle className="text-3xl font-extrabold text-slate-900 mb-4 tracking-tight">Unlock Studio Exports</DialogTitle>
              <DialogDescription className="text-base text-slate-500 font-semibold leading-relaxed px-4">
                You've reached the free trial limit. Upgrade to Pro for unlimited HD exports and premium studio features.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="p-10 space-y-6 bg-white">
            <div className="flex items-center gap-4 p-5 bg-slate-50 rounded-3xl border border-slate-100">
              <div className="w-12 h-12 rounded-2xl bg-white border border-slate-100 flex items-center justify-center text-indigo-600 flex-shrink-0 shadow-sm">
                <Sparkles size={24} />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900 leading-tight">Pro Studio Benefits</p>
                <p className="text-xs text-slate-500 font-medium mt-1">4K Rendering, Zero Watermarks, Priority Sync</p>
              </div>
            </div>

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-14 rounded-2xl text-lg font-bold shadow-xl shadow-indigo-100 transition-all flex items-center justify-center gap-2 group"
              >
                Upgrade Now
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowUpgradeModal(false)}
                className="w-full h-12 text-slate-400 hover:text-slate-600 font-bold"
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Status Modal */}
      <Dialog open={showVideoGenerator} onOpenChange={(open) => {
        if (!open) {
          setShowVideoGenerator(false);
          if (exportPollingInterval.current) clearInterval(exportPollingInterval.current);
        }
      }}>
        <DialogContent className="sm:max-w-xl bg-white rounded-[40px] border-0 shadow-2xl p-0 overflow-hidden">
          <div className="p-10">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Studio Export</h2>
                <p className="text-slate-400 text-sm font-bold uppercase tracking-widest mt-1">Processing Render</p>
              </div>
              <button onClick={() => setShowVideoGenerator(false)} className="text-slate-300 hover:text-slate-600 transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-6">
              {(exportStatus?.status === "pending" || exportStatus?.status === "queued") && (
                <div className="text-center py-10">
                  <div className="w-20 h-20 bg-amber-50 rounded-[32px] flex items-center justify-center mx-auto mb-6 text-amber-500 animate-pulse">
                    <Clock size={40} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">In the Queue</h3>
                  <p className="text-slate-500 font-medium mt-2">Hang tight! Your export is queued and will begin shortly.</p>
                </div>
              )}

              {exportStatus?.status === "processing" && (
                <div className="text-center py-10">
                  <div className="relative w-24 h-24 mx-auto mb-8">
                    <Loader2 className="w-24 h-24 text-indigo-600 animate-spin" strokeWidth={1} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Sparkles size={32} className="text-indigo-400" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Rendering Video</h3>
                  <p className="text-slate-500 font-medium mt-2 px-6">We're applying your studio effects. This usually takes less than a minute.</p>
                </div>
              )}

              {exportStatus?.status === "completed" && exportStatus.outputUrl && (
                <div className="text-center py-10">
                  <div className="w-20 h-20 bg-emerald-50 rounded-[32px] flex items-center justify-center mx-auto mb-6 text-emerald-500 shadow-lg shadow-emerald-50">
                    <CheckCircle size={40} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">Studio Master Ready!</h3>
                  <p className="text-slate-500 font-medium mt-2 px-6 mb-10">Your video has been successfully rendered and is ready for download.</p>

                  <div className="flex flex-col gap-4">
                    <a
                      href={exportStatus.outputUrl}
                      download
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-14 rounded-2xl flex items-center justify-center gap-3 font-bold text-lg shadow-xl shadow-emerald-100 transition-all"
                    >
                      <Download size={20} />
                      Download MP4
                    </a>
                    <Button
                      variant="ghost"
                      onClick={() => setShowVideoGenerator(false)}
                      className="w-full h-12 text-slate-400 hover:text-slate-900 font-bold"
                    >
                      Return to Editor
                    </Button>
                  </div>
                </div>
              )}

              {exportStatus?.status === "failed" && (
                <div className="text-center py-10">
                  <div className="w-20 h-20 bg-rose-50 rounded-[32px] flex items-center justify-center mx-auto mb-6 text-rose-500">
                    <AlertCircle size={40} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Export Unsuccessful</h3>
                  <p className="text-rose-600 font-medium mt-2 px-6 italic text-sm">
                    {exportStatus.errorMessage || "An unexpected error occurred during rendering."}
                  </p>

                  <div className="mt-10 flex flex-col gap-3">
                    <Button
                      onClick={handlePublish}
                      className="w-full bg-slate-900 hover:bg-slate-800 text-white h-12 rounded-xl font-bold shadow-lg"
                    >
                      Try Export Again
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setShowVideoGenerator(false)}
                      className="w-full h-12 text-slate-400 hover:text-slate-900 font-bold"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
